/**
 * Asking Teslyn Cloud's `document-read` function to read a picture (docs/specs/document-reading.md §2), the same call
 * from Teslyn Field, Teslyn Cloud's page and the phone app: the picture as base64 with the signed-in person's token,
 * and the answer as an outcome the screens put in words. The network is passed in (`fetch`), so the tests run it
 * with fakes and nothing here reaches the network by itself.
 */
import {
  DOCUMENT_READ_FUNCTION,
  normaliseReading,
  type DocumentKind,
  type DocumentReadMediaType,
  type DocumentReading,
} from './document-reading.js';

export interface DocumentReadClient {
  readonly supabaseUrl: string;
  /** The project's publishable key (sent as `apikey`), when the caller has one. */
  readonly publishableKey?: string;
  /** The signed-in person's access token, or null when signed out. */
  readonly token: () => Promise<string | null>;
  readonly fetch: (url: string, init: RequestInit) => Promise<Response>;
}

export type DocumentReadOutcome =
  | { readonly kind: 'read'; readonly reading: DocumentReading; readonly model: string }
  /** No reader is set up for the organisation, or the person is signed out or offline: type the values. */
  | { readonly kind: 'not_set_up' | 'signed_out' | 'offline'; readonly message: string }
  /** Refused or failed: the words of Teslyn Cloud. */
  | { readonly kind: 'refused' | 'failed'; readonly message: string };

export const READ_OFFLINE =
  'Teslyn Cloud could not be reached, so the picture was not read. Type the values from the picture, or try again when online.';
export const READ_SIGNED_OUT =
  'Sign in to Teslyn to have the picture read. Until then, type the values from the picture.';

/** Bytes as base64 (no line breaks), in chunks so a 10 MB PDF does not overflow the call stack. */
export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    out += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(out);
}

/** The media type a document file is read as: a photo fitted as JPEG, a PDF as it is. */
export function readMediaType(fileExt: string): DocumentReadMediaType {
  return fileExt.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg';
}

export async function readDocumentPicture(
  client: DocumentReadClient,
  req: {
    readonly projectId: string;
    readonly kind: DocumentKind;
    readonly mediaType: DocumentReadMediaType;
    readonly bytes: Uint8Array;
  },
): Promise<DocumentReadOutcome> {
  const token = await client.token();
  if (!token) return { kind: 'signed_out', message: READ_SIGNED_OUT };
  let res: Response;
  try {
    res = await client.fetch(
      `${client.supabaseUrl.replace(/\/+$/, '')}/functions/v1/${DOCUMENT_READ_FUNCTION}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(client.publishableKey ? { apikey: client.publishableKey } : {}),
        },
        body: JSON.stringify({
          project_id: req.projectId,
          kind: req.kind,
          media_type: req.mediaType,
          data: bytesToBase64(req.bytes),
        }),
      },
    );
  } catch {
    return { kind: 'offline', message: READ_OFFLINE };
  }
  let body: Record<string, unknown> = {};
  try {
    body = ((await res.json()) ?? {}) as Record<string, unknown>;
  } catch {
    body = {};
  }
  const words =
    typeof body.error === 'string' && body.error ? body.error : `Teslyn Cloud answered ${res.status}.`;
  if (res.ok && body.status === 'ok')
    return {
      kind: 'read',
      reading: normaliseReading(req.kind, { values: body.values, unsure: body.unsure }),
      model: typeof body.model === 'string' ? body.model : '',
    };
  if (res.status === 409 || body.status === 'not_set_up') return { kind: 'not_set_up', message: words };
  if (res.status === 401) return { kind: 'signed_out', message: READ_SIGNED_OUT };
  if (res.status >= 500 || res.status === 429) return { kind: 'failed', message: words };
  return { kind: 'refused', message: words };
}
